/**
 * PlanClarificationDialog
 *
 * A conversational UI for gathering context before plan generation.
 * Shows AI research findings and asks smart clarifying questions.
 *
 * Design principles:
 * - Conversational, not form-like
 * - Show research to build trust ("I did my homework")
 * - Only require truly necessary questions
 * - Make it fast to proceed
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  LinearProgress,
  IconButton,
  Collapse,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Science as ResearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
  Lightbulb as LightbulbIcon,
  QuestionAnswer as QuestionIcon,
} from '@mui/icons-material';
import ProgressSubMessage from './shared/ProgressSubMessage';

// ============================================================================
// Types
// ============================================================================

interface DomainFinding {
  topic: string;
  summary: string;
  details: string[];
  source?: string;
  confidence: number;
}

interface ClarifyingQuestion {
  id: string;
  question: string;
  why: string;
  type: 'text' | 'choice' | 'boolean' | 'email' | 'date';
  choices?: string[];
  required: boolean;
  defaultValue?: string;
}

interface GoalInterpretation {
  whatUserWants: string;
  measurableOutcome: string;
  implicitConstraints: string[];
}

interface DomainResearch {
  findings: DomainFinding[];
  searchQueries: string[];
  sources: string[];
  generatedAt: string;
}

interface PlanAnalysisResult {
  originalPrompt: string;
  goalInterpretation: GoalInterpretation;
  domainResearch: DomainResearch;
  clarifyingQuestions: ClarifyingQuestion[];
  readyToGenerate: boolean;
  errors?: string[];
}

type AnalysisStage =
  | 'QUEUED'
  | 'VALIDATING_INPUTS'
  | 'UPLOADING_AND_PARSING_FILES'
  | 'GENERATING_DRAFT_STRUCTURE'
  | 'FINALIZING';

const STAGE_LABELS: Record<AnalysisStage, string> = {
  QUEUED: 'Starting analysis...',
  VALIDATING_INPUTS: 'Validating inputs...',
  UPLOADING_AND_PARSING_FILES: 'Processing attachments...',
  GENERATING_DRAFT_STRUCTURE: 'Researching & analyzing...',
  FINALIZING: 'Wrapping up...',
};

const STAGE_ORDER: AnalysisStage[] = [
  'QUEUED',
  'VALIDATING_INPUTS',
  'UPLOADING_AND_PARSING_FILES',
  'GENERATING_DRAFT_STRUCTURE',
  'FINALIZING',
];

interface PlanClarificationDialogProps {
  open: boolean;
  analysis: PlanAnalysisResult | null;
  isAnalyzing: boolean;
  currentStage?: string | null;
  subMessage?: string | null;
  subTokens?: number | null;
  onClose: () => void;
  onSubmit: (answers: Record<string, string>) => void;
  onSkip: () => void;
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * Shows a single research finding with confidence indicator
 */
const ResearchFindingCard: React.FC<{ finding: DomainFinding }> = ({ finding }) => {
  const theme = useTheme();
  const confidenceColor =
    finding.confidence > 0.8 ? theme.palette.success.main :
    finding.confidence > 0.5 ? theme.palette.warning.main :
    theme.palette.grey[400];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderLeft: 3,
        borderColor: confidenceColor,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {finding.topic}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {finding.summary}
      </Typography>
      {finding.details.length > 0 && (
        <Stack spacing={0.5}>
          {finding.details.slice(0, 3).map((detail, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                color: 'text.secondary',
              }}
            >
              <span style={{ marginRight: 6 }}>•</span>
              {detail}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Renders a single clarifying question with appropriate input type
 */
const QuestionInput: React.FC<{
  question: ClarifyingQuestion;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}> = ({ question, value, onChange, autoFocus }) => {
  const theme = useTheme();

  // Boolean question - toggle buttons
  if (question.type === 'boolean') {
    return (
      <Box>
        <ToggleButtonGroup
          value={value}
          exclusive
          onChange={(_, newValue) => newValue && onChange(newValue)}
          sx={{ mb: 1 }}
        >
          <ToggleButton value="yes" sx={{ px: 4 }}>
            Yes
          </ToggleButton>
          <ToggleButton value="no" sx={{ px: 4 }}>
            No
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  }

  // Choice question - chip selection
  if (question.type === 'choice' && question.choices) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {question.choices.map((choice) => (
          <Chip
            key={choice}
            label={choice}
            onClick={() => onChange(choice)}
            variant={value === choice ? 'filled' : 'outlined'}
            color={value === choice ? 'primary' : 'default'}
            sx={{
              cursor: 'pointer',
              fontWeight: value === choice ? 600 : 400,
              transition: 'all 0.15s',
              '&:hover': {
                bgcolor: value === choice
                  ? theme.palette.primary.main
                  : alpha(theme.palette.primary.main, 0.1),
              },
            }}
          />
        ))}
      </Stack>
    );
  }

  // Text/email/date - text field
  return (
    <TextField
      fullWidth
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={question.type === 'email' ? 'email' : question.type === 'date' ? 'date' : 'text'}
      placeholder={question.type === 'email' ? 'email@example.com' : 'Type your answer...'}
      variant="outlined"
      size="small"
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
        },
      }}
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const PlanClarificationDialog: React.FC<PlanClarificationDialogProps> = ({
  open,
  analysis,
  isAnalyzing,
  currentStage,
  subMessage,
  subTokens,
  onClose,
  onSubmit,
  onSkip,
}) => {
  const theme = useTheme();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResearch, setShowResearch] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Separate required and optional questions
  const requiredQuestions = useMemo(
    () => analysis?.clarifyingQuestions.filter(q => q.required) || [],
    [analysis]
  );

  const optionalQuestions = useMemo(
    () => analysis?.clarifyingQuestions.filter(q => !q.required) || [],
    [analysis]
  );

  const allQuestions = useMemo(
    () => [...requiredQuestions, ...optionalQuestions],
    [requiredQuestions, optionalQuestions]
  );

  // Check if all required questions are answered
  const allRequiredAnswered = useMemo(
    () => requiredQuestions.every(q => answers[q.id]?.trim()),
    [requiredQuestions, answers]
  );

  // Handle answer change
  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // Handle next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  // Handle previous question
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Handle submit
  const handleSubmit = () => {
    onSubmit(answers);
  };

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setAnswers({});
      setCurrentQuestionIndex(0);
      setShowResearch(false);
    }
  }, [open]);

  // Pre-fill defaults
  React.useEffect(() => {
    if (analysis?.clarifyingQuestions) {
      const defaults: Record<string, string> = {};
      analysis.clarifyingQuestions.forEach(q => {
        if (q.defaultValue) {
          defaults[q.id] = q.defaultValue;
        }
      });
      setAnswers(prev => ({ ...defaults, ...prev }));
    }
  }, [analysis]);

  const currentQuestion = allQuestions[currentQuestionIndex];
  const isOnRequiredQuestion = currentQuestionIndex < requiredQuestions.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
          color: 'white',
          px: 3,
          py: 2.5,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            opacity: 0.8,
            '&:hover': { opacity: 1 },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AIIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isAnalyzing ? 'Analyzing your goal...' : 'Quick questions before we start'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {isAnalyzing
                ? 'Researching best practices and requirements'
                : 'Help me create a better plan for you'}
            </Typography>
          </Box>
        </Stack>

        {isAnalyzing && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'white',
              },
            }}
          />
        )}
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Loading State */}
        {isAnalyzing && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Stack spacing={3} alignItems="center">
              <ResearchIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Doing my homework...
                </Typography>
                {subMessage ? (
                  <ProgressSubMessage message={subMessage} tokens={subTokens} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {(currentStage && STAGE_LABELS[currentStage as AnalysisStage])
                      || 'Searching for requirements, best practices, and what you\'ll need'}
                  </Typography>
                )}
              </Box>
              {currentStage && (
                <LinearProgress
                  variant="determinate"
                  value={Math.max(
                    10,
                    ((STAGE_ORDER.indexOf(currentStage as AnalysisStage) + 1) / STAGE_ORDER.length) * 100
                  )}
                  sx={{ width: '60%', borderRadius: 1, height: 6 }}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Analysis Results */}
        {!isAnalyzing && analysis && (
          <Box>
            {/* Goal Interpretation */}
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Alert
                severity="success"
                icon={<LightbulbIcon />}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  I understand you want to:
                </Typography>
                <Typography variant="body2">
                  {analysis.goalInterpretation.whatUserWants}
                </Typography>
                {analysis.goalInterpretation.measurableOutcome && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Success: {analysis.goalInterpretation.measurableOutcome}
                  </Typography>
                )}
              </Alert>
            </Box>

            {/* Research Findings (Collapsible) */}
            {analysis.domainResearch.findings.length > 0 && (
              <Box sx={{ px: 3, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<ResearchIcon />}
                  endIcon={showResearch ? <CollapseIcon /> : <ExpandIcon />}
                  onClick={() => setShowResearch(!showResearch)}
                  sx={{ textTransform: 'none', mb: 1 }}
                >
                  {showResearch ? 'Hide' : 'Show'} what I found ({analysis.domainResearch.findings.length} insights)
                </Button>

                <Collapse in={showResearch}>
                  <Stack spacing={1.5}>
                    {analysis.domainResearch.findings.map((finding, i) => (
                      <ResearchFindingCard key={i} finding={finding} />
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}

            <Divider />

            {/* Questions Section */}
            {allQuestions.length > 0 ? (
              <Box sx={{ px: 3, py: 3 }}>
                {/* Progress indicator */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <QuestionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Question {currentQuestionIndex + 1} of {allQuestions.length}
                      {!isOnRequiredQuestion && ' (optional)'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    {allQuestions.map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: i === currentQuestionIndex
                            ? 'primary.main'
                            : answers[allQuestions[i].id]
                              ? 'success.main'
                              : 'grey.300',
                          transition: 'all 0.2s',
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>

                {/* Current Question */}
                <Fade key={currentQuestion.id} in timeout={300}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                      {currentQuestion.question}
                      {currentQuestion.required && (
                        <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                          *
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {currentQuestion.why}
                    </Typography>

                    <QuestionInput
                      question={currentQuestion}
                      value={answers[currentQuestion.id] || ''}
                      onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                      autoFocus
                    />
                  </Box>
                </Fade>

                {/* Navigation */}
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
                  <Button
                    onClick={handlePrevQuestion}
                    disabled={currentQuestionIndex === 0}
                    sx={{ visibility: currentQuestionIndex === 0 ? 'hidden' : 'visible' }}
                  >
                    Back
                  </Button>

                  <Stack direction="row" spacing={2}>
                    {currentQuestionIndex < allQuestions.length - 1 ? (
                      <>
                        {!isOnRequiredQuestion && (
                          <Button
                            variant="text"
                            onClick={handleNextQuestion}
                            sx={{ color: 'text.secondary' }}
                          >
                            Skip
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          onClick={handleNextQuestion}
                          disabled={isOnRequiredQuestion && !answers[currentQuestion.id]?.trim()}
                          endIcon={<ArrowIcon />}
                        >
                          Next
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!allRequiredAnswered}
                        endIcon={<CheckIcon />}
                        sx={{ minWidth: 140 }}
                      >
                        Create Plan
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Box>
            ) : (
              /* No questions needed */
              <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  I have everything I need!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Based on my research, I can create a comprehensive plan for you.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onSubmit({})}
                  endIcon={<ArrowIcon />}
                >
                  Create My Plan
                </Button>
              </Box>
            )}

            {/* Skip option */}
            {allQuestions.length > 0 && (
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  bgcolor: 'grey.50',
                  borderTop: 1,
                  borderColor: 'divider',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Want to skip these questions?{' '}
                  <Button
                    size="small"
                    onClick={onSkip}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                  >
                    Generate a basic plan instead
                  </Button>
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Error State */}
        {!isAnalyzing && analysis?.errors && analysis.errors.length > 0 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                I couldn't complete all the research, but I can still help!
              </Typography>
            </Alert>
            <Button variant="contained" onClick={() => onSubmit({})}>
              Continue anyway
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlanClarificationDialog;

